import type { ChannelPermission } from "@f3/shared/permissions";
import { useEventBus, type EventBusKey } from "@vueuse/core";
import { watchEffect } from "vue";
import type {
  AuthChannelType,
  ConversionType,
  User,
  ValidatedUserIdentifier,
} from "@/generated/graphql";
import type { AuthorizationRuleForGating } from "@/features/gating/use-gating-permissions";
import type { UserStoreMe } from "@/stores/user.store";
import type { GatingStrategyType } from "@/features/gating/strategies/gating-strategy";

export interface GatingRequestData {
  requestedPermission?: ChannelPermission | undefined;
  requestedAuthorizationRule?: AuthorizationRuleForGating | undefined;
}

export interface AuthModalRequestData extends GatingRequestData {
  referrer?: User | undefined;
}

export enum ChannelEventType {
  SHOW_AUTH_MODAL = "show_auth_modal",
  SHOW_UPGRADE_MODAL = "show_upgrade_modal",
  SHOW_SUPERFAN_MODAL = "show_superfan_modal",
  USER_IDENTIFIED = "user_identified",
  AUTHENTICATION_FINISHED = "authentication_finished",
  SIGN_UP_WITH_IDENTIFIERS = "sign_up_with_identifiers",
  UPGRADE_FINISHED = "upgrade_finished",
}

export interface ShowAuthModalEvent extends AuthModalRequestData {
  type: ChannelEventType.SHOW_AUTH_MODAL;
}

export interface SignUpWithIdentifiersEvent {
  type: ChannelEventType.SIGN_UP_WITH_IDENTIFIERS;
  validatedIdentifiers: ValidatedUserIdentifier[];
  authChannelType: AuthChannelType;
}

export interface ShowUpgradeEvent extends GatingRequestData {
  type: ChannelEventType.SHOW_UPGRADE_MODAL;
  selectedUpgradeObjectId?: string;
  gatingStrategyType?: GatingStrategyType;
}

export interface UserIdentifiedEvent {
  type: ChannelEventType.USER_IDENTIFIED;
  user: UserStoreMe;
}

export interface AuthenticationFinishedEvent {
  type: ChannelEventType.AUTHENTICATION_FINISHED;
  willUpgrade: boolean;
}

export interface UpgradeFinishedEvent {
  type: ChannelEventType.UPGRADE_FINISHED;
  conversionType: ConversionType;
}

export interface ShowSuperfanModalEvent {
  type: ChannelEventType.SHOW_SUPERFAN_MODAL;
}

export type ChannelAuthEvent =
  | ShowAuthModalEvent
  | ShowUpgradeEvent
  | ShowSuperfanModalEvent
  | UserIdentifiedEvent
  | AuthenticationFinishedEvent
  | SignUpWithIdentifiersEvent
  | UpgradeFinishedEvent;

const channelAuthEventBusKey: EventBusKey<ChannelAuthEvent> = Symbol(
  "channelAuthEventBus",
);

export function useChannelEvents() {
  const bus = useEventBus(channelAuthEventBusKey);

  function emitChannelEvent(event: ChannelAuthEvent) {
    bus.emit(event);
  }

  function watchEventsEffect(listener: (event: ChannelAuthEvent) => void) {
    return watchEffect(() => {
      bus.on(listener);

      return () => {
        bus.off(listener);
      };
    });
  }

  return {
    emitChannelEvent,
    watchEventsEffect,
  };
}
